import { isEmpty } from 'lodash';

/**
 * Billing state for insurance quotes usually comes from the card billing form or saved card.
 * When checkout uses full-page Stripe or an express wallet (no card address on this page), the guest
 * picks their US state here so `refreshInsuranceQuote` can use it — same mechanism as the card form,
 * without persisting extra fields to the booking payload.
 */
export function needsInsuranceQuoteResidencyStateDropdown(params: {
  stripeRegionOk: boolean;
  suppressStripePaymentUI: boolean;
  showExpressCheckoutFlow: boolean;
  cardHasSavedPaymentMethod: boolean;
  isExpressWalletPaymentSelected: boolean;
}): boolean {
  const {
    stripeRegionOk,
    suppressStripePaymentUI,
    showExpressCheckoutFlow,
    cardHasSavedPaymentMethod,
    isExpressWalletPaymentSelected,
  } = params;

  if (!stripeRegionOk || cardHasSavedPaymentMethod) {
    return false;
  }
  if (suppressStripePaymentUI) {
    return true;
  }
  return showExpressCheckoutFlow && isExpressWalletPaymentSelected;
}

/** Mirrors `Insurance` wrapper visibility (see insurance-wrapper): offer shown and user must choose yes/no. */
export function isInsuranceBookingSelectionMissing(
  showInsuranceGuestSection: boolean,
  enableInsurance: boolean | undefined,
  insurance: unknown,
  insuranceLoading: boolean,
  insuranceError: unknown,
  isRemoveInsurance: boolean | undefined,
  selection: unknown,
): boolean {
  const visible =
    showInsuranceGuestSection &&
    !!enableInsurance &&
    !!insurance &&
    !insuranceLoading &&
    isEmpty(insuranceError) &&
    !isRemoveInsurance;

  return visible && (selection === null || selection === undefined);
}

/**
 * The RCI/condo side of the API spells the United States 'USA' where the hotel side uses the ISO-2
 * 'US' (see condo-static: rciCountries are matched on the name 'USA', intervalCountries on the code
 * 'USA'). Normalises either spelling to the ISO-2 the insurance provider expects, and returns
 * undefined for anything that is not a country code, so callers can fail loudly rather than quote
 * against a destination we guessed.
 */
export function normalizeIsoCountry(value?: string | null): string | undefined {
  const code = value?.trim().toUpperCase();
  if (!code) return undefined;
  if (code === 'US' || code === 'USA') return 'US';
  return code.length === 2 ? code : undefined;
}

/**
 * Insurance is only offered to US residents, but the bookings API spells the main guest's country
 * differently per product: hotels fill `countryCode` with 'US', condos leave it (and `country`)
 * empty and only fill `residence`, with 'USA'. Reading `countryCode` alone therefore never matched
 * a condo guest, so "Protect reservation" was never offered for condos (TRAV23-4983).
 */
export function isUsResidencyGuest(guest: {
  countryCode?: string;
  country?: string;
  residence?: string;
} | null | undefined): boolean {
  return [guest?.countryCode, guest?.country, guest?.residence]
    .some((value) => normalizeIsoCountry(value) === 'US');
}

/**
 * Destination country for an insurance quote. `GET /bookings/content` answers in two shapes: hotels
 * carry `countryCode` ('US'), condos omit it entirely and carry only `countryName` ('USA'). Reading
 * `countryCode` alone dropped `destinationIsoCountryCode` from the condo quote, which the provider
 * rejects as invalid_payload (TRAV23-4983).
 */
export function resolveDestinationIsoCountry(staticData: {
  countryCode?: string;
  countryName?: string;
} | null | undefined): string | undefined {
  return normalizeIsoCountry(staticData?.countryCode) ?? normalizeIsoCountry(staticData?.countryName);
}
